/**
 * This module should contain common enums shared with the backend.
 */

/*
 * Auth
 */
export enum AuthErrorCode {
    INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
    TOKEN_EXPIRED = 'TOKEN_EXPIRED',
}

/*
 * Users
 */
export enum UserRole {
    CLIENT = 'Client',
    PROVIDER = 'Provider',
    SHARED = 'Shared',

    // Admin Roles
    SUPER_ADMIN = 'SuperAdmin',
    BILLING_ADMIN = 'BillingAdmin',
    CLINICAL_SUPERVISOR = 'ClinicalSupervisor',
    RECRUITER = 'Recruiter',
    CREDENTIALER = 'Credentialer',
    SCHEDULING_ADMIN = 'SchedulingAdmin',
    CC_MANAGER = 'CareCoordinationManager',
    CARE_COORDINATOR = 'CareCoordinator',
    SUPPORT_SPECIALIST = 'SupportSpecialist',

    // Internal admin for acquired network-partner staff. Carries the same
    // permissions as BILLING_ADMIN; rendered with the PayerAdmin experience.
    NETWORK_PARTNER_ADMIN = 'NetworkPartnerAdmin',

    // Organization Roles
    ORGANIZATION_ADMIN = 'OrganizationAdmin',
    PAYER_ADMIN = 'PayerAdmin',
}

export enum COUNTRY_SPECIFIC_FEATURES {
    THERAPY = 'Therapy',
}

export enum UserPrivilege {}

export enum DevicePlatform {
    WEB_DESKTOP = 'web_desktop',
    WEB_MOBILE = 'web_mobile',
    MOBILE_IOS = 'mobile_ios',
    MOBILE_ANDROID = 'mobile_android',
    MOBILE_OTHER = 'mobile_other',
}

export enum VideoCallBuild {
    AARON = 'aaron',
    RAY = 'ray',
    WHEREBY = 'whereby',
}

export enum Gender {
    MALE = 'Male',
    FEMALE = 'Female',
    OTHER = 'Other',
}

export enum ClientStatus {
    PENDING = 'Pending',
    PAUSED = 'Paused',
    CUSTOMER = 'Customer',
    CANCELED = 'Canceled',
    LEAD = 'Lead',
}

export enum ProviderStatus {
    LEAD = 'Lead',
    ONBOARDING = 'Onboarding',
    IN_TRAINING = 'InTraining',
    HIRED = 'Hired',
}

export enum ProviderRecruitingStatus {
    // Initial status
    INTERESTED = 'Interested',
    INTRO_CALL_SCHEDULED = 'IntroCallScheduled',
    INTRO_CALL_COMPLETED = 'IntroCallCompleted',
    INTERVIEW_SCHEDULED = 'InterviewScheduled',
    CONTRACT_SENT = 'ContractSent',

    // Recruiting complete
    CONTRACT_SIGNED = 'ContractSigned',

    // Other
    PAUSED = 'Paused',
    REJECTED = 'Rejected',
    /**
     * The clinician declined — distinct from REJECTED, which is us
     * disqualifying them. Set automatically when an inbound reply is
     * classified as not interested. Recoverable: successful objection
     * handling moves them back to INTERESTED.
     */
    NOT_INTERESTED = 'NotInterested',
}

export enum AvailableProviderSortBy {
    CREATED_AT = 'createdAt',
    NAME = 'name',
    POPULARITY = 'popularity',
    RATINGS = 'ratings',
}

/**
 * Provider-agnostic mirror of a contract's e-signature status (backend
 * `ContractStatus`). Exposed over GraphQL as a plain string. `Failed` is
 * local-only — set when our send-side request fails before the e-signature
 * vendor (Dropbox Sign) assigns a status.
 */
export enum ContractStatus {
    CREATED = 'Created',
    SENT = 'Sent',
    VIEWED = 'Viewed',
    COMPLETED = 'Completed',
    DECLINED = 'Declined',
    CANCELED = 'Canceled',
    EXPIRED = 'Expired',
    FAILED = 'Failed',
}

/**
 * Calendly link variants generated by the recruiting flow. String
 * values mirror the backend `CalendlyLinkType` (and the GraphQL enum).
 */
export enum CalendlyLinkType {
    INTRO_CALL = 'INTRO_CALL',
    INTERVIEW = 'INTERVIEW',
}

export enum ClientSource {
    WEBSITE = 'Website',
    BARK = 'Bark',
    ZOCDOC = 'Zocdoc',
    PSYCH_TODAY = 'Psychology Today',
    COMPSYCH = 'ComPsych',
    ORGANIZATION = 'Organization',
    EAP = 'EAP',
    EMPATHIA = 'Empathia',
}

export enum ProviderSource {
    WEBSITE = 'Website',
    NEWSLETTER = 'Newsletter',
    ONBOARDING = 'Onboarding',
}

export enum ProviderService {
    THERAPY = 'Therapy',
    COACHING = 'Coaching',
}

export enum ProviderCarrierStatus {
    APPLIED = 'Applied',
    PANELED = 'Paneled',
    NON_APPLICABLE = 'NonApplicable',
}

export enum SessionType {
    INDIVIDUAL = 'Individual',
    COUPLES = 'Couples',
    FAMILY = 'Family',
}

/**
 * How care is delivered, as distinct from `SessionType`, which is who is in
 * the room. Video is the primary mode; phone exists for clients who cannot
 * hold a video call.
 *
 * Mirrors the backend enum of the same name. The two are hand-duplicated with
 * no sync tooling, as `SessionType` above already is.
 */
export enum SessionMode {
    VIDEO = 'Video',
    PHONE = 'Phone',
}

export enum ProviderPayRateType {
    INDIVIDUAL = 'Individual',
    COUPLES = 'Couples',
    FAMILY = 'Family',
    EAP = 'EAP',
    GROUPS = 'Groups',
}

export enum TagSeverity {
    INFO = 'info',
    SUCCESS = 'success',
    WARNING = 'warning',
    ERROR = 'error',
}

export enum ProviderSuspensionReason {
    NO_SHOWS = 'NoShows',
    INCOMPLETE_NOTES = 'IncompleteNotes',
    MONTHLY_REVIEW = 'MonthlyReview',
    TERMINATION = 'Termination',
    RESIGNATION = 'Resignation',
    UNFIT_FOR_NEW_CLIENTS = 'UnfitForNewClients',
}

export enum ClientSuspensionReason {
    OWED_BALANCE = 'OwedBalance',
    TERMED_COVERAGE = 'TermedCoverage',
    CLAIM_DENIED = 'ClaimDenied',
    LOW_PAYING_PLAN = 'LowPayingPlan',
    EXPIRED_EAP = 'NoEAPSessionsRemaining',
    EAP_NO_SHOW = 'EAPNoShow',
    NO_PAYMENT_METHOD = 'NoPaymentMethod',
    NOT_SUITED_FOR_THERAPY = 'NotSuitedForTherapy',
}

export enum ClientTodoType {
    COMPLETE_REGISTRATION = 'CompleteRegistration',
    BOOK_FIRST_SESSION = 'BookFirstSession',
    TAKE_BASELINE_ASSESSMENTS = 'TakeBaselineAssessments',
    VERIFY_EMAIL = 'VerifyEmail',
    ATTEND_FIRST_SESSION = 'AttendFirstSession',

    WRITE_JOURNAL = 'WriteJournal',
    TAKE_ASSESSMENTS = 'TakeAssessments',
    BOOK_SESSION = 'BookSession',
    ATTEND_SESSION = 'AttendSession',
    REVIEW_PROVIDER = 'ReviewProvider',
}

export enum AvailabilityOfferStatus {
    OPEN = 'open',
    ACCEPTED = 'accepted',
    REJECTED = 'rejected',
    CANCELED = 'canceled',
    EMPTY = 'empty',
}

/*
 * Organizations
 */

export enum OrganizationStatus {
    PENDING = 'Pending',
    ACTIVE = 'Active',
    SUSPENDED = 'Suspended',
    TERMINATED = 'Terminated',
}

export enum OrganizationMemberStatus {
    ACTIVE = 'Active',
    TERMINATED = 'Terminated',
    REMOVED = 'Removed',
}

export enum OrganizationGroupType {
    DEPARTMENT = 'Department',
    TEAM = 'Team',
    LOCATION = 'Location',
}

/*
 * Fax
 */
export enum FaxJobHistoryStatus {
    CANCELLED = 'cancelled',
    COMPLETE = 'complete',
    FAILED = 'failed',
    IN_PROGRESS = 'in_progress',
    QUEUED = 'queued',
}
export enum FaxJobHistoryType {
    FAXING_DOCUMENTS = 'faxing_documents',
    GENERATING_PDFS = 'generating_pdfs',
    WAITING_FOR_WORKER = 'waiting_for_worker',
}

/*
 * Files
 */
export enum UploadType {
    USER_FILE = 'user_file',
    ORGANIZATION_FILE = 'organization_file',
    DOCUMENT = 'document',
    GROUP_FILE = 'group_file',
    PROVIDER_FEEDBACK_FILE = 'provider_feedback_file',
}

export enum UserFileType {
    PROFILE_PICTURE = 'profile_picture',
    CAREBOT_AVATAR = 'carebot_avatar',
    INSURANCE_CARD_FRONT = 'insurance_card_front',
    INSURANCE_CARD_BACK = 'insurance_card_back',
}

export enum OrganizationFileType {
    PROFILE_PICTURE = 'profile_picture',
    LOGO = 'logo',
}

export enum ProviderFeedbackFileType {
    ATTACHMENT = 'provider_feedback_attachment',
}

export enum DocumentType {
    RELEASE_ATTACHMENT = 'release',
    UPLOAD = 'upload',
}

/**
 * Tags a document as one of a provider's credentialing files. Null for ordinary uploads.
 * A provider may hold any number of documents of each type — which one currently fills a
 * credentialing slot is recorded by the `*DocumentId` fields on their credentialing data.
 */
export enum CredentialDocumentType {
    RESUME = 'resume',
    HEADSHOT = 'headshot',
    LIABILITY_INSURANCE = 'liabilityInsurance',
    PHOTO_ID = 'photoId',
    LICENSE = 'license',
    DIPLOMA_OR_TRANSCRIPTS = 'diplomaOrTranscripts',
    CONSENT_AND_COMPLIANCE = 'consentAndCompliance',
}

/**
 * The sections of the credentialing onboarding card, in the order it renders them.
 * Each is a group of fields the provider fills in together, not a single column.
 */
export enum CredentialingSection {
    SSN = 'ssn',
    PRIMARY_TAXONOMY = 'primaryTaxonomy',
    /** CAQH username and password. The CAQH ID number is asked for on the consent card */
    CAQH = 'caqh',
    EDUCATION = 'education',
    DOCUMENTS = 'documents',
    LICENSES = 'licenses',
    CONSENT = 'consent',
}

/**
 * How far along a section of the credentialing onboarding card is.
 * `PARTIAL` never comes back for a one-field section — those go straight to `COMPLETED`.
 */
export enum CredentialingSectionStatus {
    /** Nothing filled in yet */
    PENDING = 'pending',
    PARTIAL = 'partial',
    COMPLETED = 'completed',
    /** Licenses only: at least one license is past its expiration date */
    EXPIRED = 'expired',
}

/**
 * Where a single state license stands against its expiration date. Describes one license,
 * unlike CredentialingSectionStatus, which describes the group.
 */
export enum LicenseExpirationStatus {
    ACTIVE = 'active',
    /** Still valid, but expiring within 90 days */
    EXPIRING_SOON = 'expiringSoon',
    /** Expired, or expiring today */
    EXPIRED = 'expired',
    /** No expiration date recorded, so there is nothing to measure against */
    UNKNOWN = 'unknown',
}

export enum GroupFileType {
    PROFILE_PICTURE = 'group_profile_picture',
}

/** Insurance carrier co-branding images (entityId is the insuranceCarrierId). */
export enum CarrierFileType {
    /** The full logo, with the carrier's name */
    LOGO = 'carrier_logo',
    /** The mini logo: the mark alone, without text */
    LOGO_ICON = 'carrier_logo_icon',
}

/*
 * Appointments
 */
export enum AppointmentStatus {
    PENDING = 'Pending',
    CONFIRMED = 'Confirmed',
    OCCURRED = 'Occurred',
    CANCELED = 'Canceled',
    MISSED_PROVIDER = 'MissedProvider',
    MISSED_CLIENT = 'MissedClient',
}

export enum AppointmentPayoutStatus {
    NOT_PAID_OUT = 'NotPaidOut',
    PAID_OUT = 'PaidOut',
}

export enum AppointmentDxStatus {
    INCOMPLETE = 'Incomplete',
    CODES_SUBMITTED = 'Codes Submitted',
    INCOMPLETE_NOTES = 'Incomplete Notes',
    COMPLETE = 'Complete',
    NA = 'N/A',
}

export enum AppointmentActionType {
    CONFIRM = 'Confirm',
    CANCEL = 'Cancel',
    RESCHEDULE = 'Reschedule',
    MARK_AS_OCCURED = 'MarkAsOccured',
    OVERRIDE = 'Override',
    CHARGE_FEE = 'ChargeFee',
    CHARGE_CUSTOM_PAYMENT = 'ChargeCustomPayment',
}

export enum AppointmentActionOptionType {
    LEAVE_PENDING = 'LeavePending',
    WAIVE_FEE = 'WaiveFee',
    NOTIFY_CLIENT = 'NotifyClient',
    NOTIFY_PROVIDER = 'NotifyProvider',
    MOVED_BY_PROVIDER = 'MovedByProvider',
    BYPASS_DAILY_LIMIT = 'BypassDailyLimit',
    SEND_CARE_TEAM_SMS = 'SendCareTeamSMS',
}

export enum PayoutStatus {
    TRANSFER_FAILED = 'Transfer_Failed',
    TRANSFERRED = 'Transferred',
    PAID = 'Paid',
    /** Payout was accepted by Stripe but later failed/canceled (e.g. bank rejection) */
    PAYOUT_FAILED = 'Payout_Failed',
}

export enum PayoutType {
    COMMERCIAL = 'Commercial',
    EAP = 'EAP',
    FEES = 'Fees',
    GROUPS = 'Groups',
    ALL = 'All',
    CUSTOM = 'Custom',
}

export enum ProviderListingTagColor {
    RED = 'red',
    ORANGE = 'orange',
    YELLOW = 'yellow',
    GREEN = 'green',
    GRAY = 'gray',
}

export enum ProviderListingSortBy {
    NAME = 'name',
    RATING = 'rating',
    POPULARITY = 'popularity',
}

export enum ZoomFileType {
    MP4 = 'MP4',
    M4A = 'M4A',
    TIMELINE = 'TIMELINE',
    TRANSCRIPT = 'TRANSCRIPT',
    CHAT = 'CHAT',
    CC = 'CC',
    CSV = 'CSV',
}

export enum ZoomRecordingType {
    SPEAKER_VIEW = 'speaker_view',
    GALLERY_VIEW = 'gallery_view',
    SHARED_SCREEN = 'shared_screen',
    AUDIO_ONLY = 'audio_only',
    AUDIO_TRANSCRIPT = 'audio_transcript',
    CHAT_FILE = 'chat_file',
    ACTIVE_SPEAKER = 'active_speaker',
    TIMELINE = 'timeline',
    SUMMARY = 'summary',
}

// Supported Zoom session event types
export enum ZoomSessionEventType {
    SESSION_ALERT = 'session.alert',
    SESSION_STARTED = 'session.started',
    SESSION_ENDED = 'session.ended',
    SESSION_USER_JOINED = 'session.user_joined',
    SESSION_USER_LEFT = 'session.user_left',
    SESSION_RECORDING_COMPLETED = 'session.recording_completed',
    SESSION_RECORDING_STARTED = 'session.recording_started',
    SESSION_RECORDING_STOPPED = 'session.recording_stopped',
    SESSION_RECORDING_TRANSCRIPT_COMPLETED = 'session.recording_transcript_completed',
    SESSION_RECORDING_TRANSCRIPT_FAILED = 'session.recording_transcript_failed',
}

export enum ReachLinkSessionEventType {
    USER_JOINED = 'user_joined',
    USER_LEFT = 'user_left',
    AUDIO_TOGGLED = 'audio_toggled',
    MIC_TOGGLED = 'mic_toggled',
    CAMERA_TOGGLED = 'camera_toggled',
    SCREEN_SHARE_TOGGLED = 'screen_share_toggled',
    MODAL_TOGGLED = 'modal_toggled',
    CHAT_MESSAGE_SENT = 'chat_message_sent',
    INPUT_DEVICE_CHANGED = 'input_device_changed',
    AUDIO_OUTPUT_CHANGED = 'audio_output_changed',
    SETTINGS_TAB_CLICKED = 'settings_tab_clicked',
    MIRROR_VIDEO_TOGGLED = 'mirror_video_toggled',
    VIRTUAL_BACKGROUND_CHANGED = 'virtual_background_changed',
    SCREEN_SHARE_SETTING_CHANGED = 'screen_share_setting_changed',
    LAYOUT_CHANGED = 'layout_changed',
    FULLSCREEN_TOGGLED = 'fullscreen_toggled',
    TIMEOUT_POPUP_APPEARED = 'timeout_popup_appeared',
    TIMEOUT_POPUP_DISMISSED = 'timeout_popup_dismissed',
    SETTINGS_GROUP_TOGGLED = 'settings_group_toggled',
}

export enum ReachlinkSessionWherebyEventType {
    GRANT_DEVICE_PERMISSION = 'grant_device_permission',
    DENY_DEVICE_PERMISSION = 'deny_device_permission',
    PRECALL_CHECK_COMPLETED = 'precall_check_completed',
    PRECALL_CHECK_SKIPPED = 'precall_check_skipped',
    MEETING_END = 'meeting_end',
    JOIN = 'join', //local join
    LEAVE = 'leave', //local leave
    PARTICIPANT_JOIN = 'participant_join',
    PARTICIPANT_LEAVE = 'participant_leave',
    MICROPHONE_TOGGLE = 'microphone_toggle',
    CAMERA_TOGGLE = 'camera_toggle',
    CHAT_TOGGLE = 'chat_toggle',
    PEOPLE_TOGGLE = 'people_toggle',
    PIP_TOGGLE = 'pip_toggle',
    SCREENSHARE_TOGGLE = 'screenshare_toggle',
    STREAMING_STATUS_CHANGE = 'streaming_status_change',
    RECORDING_STATUS_CHANGE = 'recording_status_change',
    TRANSCRIPTION_STATUS_CHANGE = 'transcription_status_change',
    CONNECTION_STATUS_CHANGE = 'connection_status_change',
}

/**
 * Phone (Call Connect) session event types, appended to `SessionData.eventLogs`
 * so the Session Data tab shows a timestamped log of phone-call events alongside
 * video events.
 */
export enum PhoneSessionEventType {
    CALL_STARTED = 'Phone.call.started',
    PROVIDER_CONNECTED = 'Phone.call.provider.connected',
    CLIENT_CONNECTED = 'Phone.call.client.connected',
    RETRY = 'Phone.call.retry',
    DECLINED = 'Phone.call.declined',
    ENDED = 'Phone.call.ended',
    TRANSCRIPTION_STARTED = 'Phone.transcription.started',
    TRANSCRIPTION_FINISHED = 'Phone.transcription.finished',
}

export type ReachLinkSessionEvent =
    | ReachLinkSessionEventType
    | ReachlinkSessionWherebyEventType
    | PhoneSessionEventType;

/*
 * Assessments
 */
export enum AssessmentType {
    PHQ9 = 'phq-9',
    GAD7 = 'gad-7',
    PROMIS_ANGER = 'promis-anger',
    PROMIS_SLEEP = 'promis-sleep-disturbance',
    AWARE = 'aware',
    VC_FEEDBACK = 'vc-feedback',
    PROVIDER_REVIEW = 'provider-review',
    AUDIT_C = 'audit-c',
    DAST_10 = 'dast-10',
    SIP_AD_5 = 'sip-ad-5',
}

/*
 * Claims & Coverage
 */
export enum CoverageType {
    SELF_PAY = 'SelfPay',
    INSURANCE = 'Insurance',
    EAP = 'EAP',
}

export enum PolicyType {
    STANDARD = 'Standard',
    EAP = 'EAP',
}

export enum EncounterType {
    APPOINTMENT = 'appointment',
    ASSESSMENT = 'assessment',
}

export enum InsuranceCarrierType {
    STANDARD = 'Standard',
    EAP = 'EAP',
    NON_PAYOR = 'NonPayor',
}

export enum InsuranceClaimStatus {
    UNSUBMITTED = 'Unsubmitted',
    SUBMITTED = 'Submitted',
    DENIED = 'Denied',
    PAID = 'Paid',
}

export enum InsuranceClaimFlag {
    RL_FLAG = 'Flagged by ReachLink',
    CH_FLAG = 'Flagged by Clearinghouse',
    PAYER_FLAG = 'Flagged by Payer',
}

export enum InsuranceClaimNote {
    PROVIDER_NOT_ELIGIBLE = 'Provider Not Eligible',
    NO_OON = 'No OON',
    COVERAGE_EXPIRED = 'Coverage Expired',
    MEDICAL_RECORDS = 'Medical Records',
    NO_AUTHORIZATION = 'No Authorization',
    INVALID_DIAGNOSIS = 'Invalid Diagnosis',
    DEDUCTIBLE = 'Deductible',
    TIMELY_FILING = 'Timely Filing',
}

export enum ClaimDxStatus {
    NO_CODES = 'No Codes',
    CODES_NOT_SUBMITTED = 'Codes Not Submitted',
    CODES_SUBMITTED = 'Codes Submitted',
}

export enum ClaimResubmissionType {
    CORRECTION = 'correction',
    ORIGINAL = 'original',
}

export enum ClearingHouse {
    WAYSTAR = 'Waystar',
    CLAIM_MD = 'Claim MD',
    CUSTOM_API = 'Custom API',
    NONE = 'None',
}

export enum PolicyMemberRelationship {
    SELF = 'Self',
    SPOUSE = 'Spouse',
    CHILD = 'Child',
    OTHER = 'Other',
}

/*
 * Verification of Benefits (VOB)
 */
export enum VOBType {
    // Legacy
    STANDARD = 'standard',
    INITIAL = 'initial',
    RENEWAL = 'renewal',
}

export enum VOBStatus {
    PENDING = 'pending',
    COMPLETE = 'complete',
    FAILED = 'failed',
}

export enum VOBSource {
    SOHAR = 'sohar',
}

export enum VOBOutcome {
    COPAY_UPDATED = 'copay_updated',
    COPAY_UNCHANGED = 'copay_unchanged',
    COVERAGE_TERMED = 'coverage_termed',
    ESCALATED = 'escalated',
}

/*
 * Payments
 */
export enum Currency {
    USD = 'usd',
    GBP = 'gbp',
    EUR = 'eur',
    CAD = 'cad',
    AUD = 'aud',
    NZD = 'nzd',
    JPY = 'jpy',
    HKD = 'hkd',
    SGD = 'sgd',
    MXN = 'mxn',
    CHF = 'chf',
    SEK = 'sek',
    DKK = 'dkk',
    PLN = 'pln',
    NOK = 'nok',
    HUF = 'huf',
    CZK = 'czk',
    ILS = 'ils',
    BRL = 'brl',
    MYR = 'myr',
    PHP = 'php',
    TWD = 'twd',
    THB = 'thb',
    ZAR = 'zar',
}

export enum PaymentMethodType {
    CARD = 'card',
    BANK_TRANSFER = 'bank_transfer',
}

export enum PaymentStatus {
    SUCCEEDED = 'succeeded',
    REFUNDED = 'refunded',
    FAILED = 'failed',
    UNPAID = 'unpaid',
}

export enum PaymentType {
    APPOINTMENT_LATE_FEE = 'appointment_late_fee',
    ORGANIZATION_SUBSCRIPTION = 'organization_subscription',
    REACHLINK_PLUS_SUBSCRIPTION = 'reachlink_plus_subscription',
    INITIAL_APPOINTMENT_FEE = 'initial_appointment_fee',
    APPOINTMENT_FEE = 'appointment_fee',
    APPOINTMENT_TIP = 'appointment_tip',
    CUSTOM_CHARGE = 'custom_charge',
    GROUP_SUBSCRIPTION_CHARGE = 'group_subscription_charge',
}

/*
 * Journals
 */
export enum JournalMood {
    HORRIBLE,
    POOR,
    FAIR,
    GOOD,
    EXCELLENT,
}

export enum JournalCategory {
    RELATIONSHIPS = 'Relationships',
    CAREER = 'Career',
    HEALTH = 'Health',
    FINANCE = 'Finance',
    PERSONAL_GROWTH = 'Personal Growth',
    FAMILY = 'Family',
    SOCIAL_LIFE = 'Social Life',
    HOME_ENVIRONMENT = 'Home Environment',
    EDUCATION = 'Education',
    SPIRITUALITY = 'Spirituality',
    OTHER = 'Other',
}

export enum JournalOutcome {
    WORSE,
    SAME,
    BETTER,
}

export enum EmotionType {
    NEGATIVE,
    POSITIVE,
}

export enum JournalInferenceStatus {
    PROCESSING = 'processing',
    COMPLETED = 'completed',
    FAILED = 'failed',
}

export enum TriggerType {
    POSITIVE = 'positive',
    NEGATIVE = 'negative',
}

export enum JournalSubcategory {
    // Relationships
    ROMANTIC_PARTNERSHIP = 'Romantic Partnership',
    DATING_CHALLENGES = 'Dating Challenges',
    COMMUNICATION_ISSUES = 'Communication Issues',
    TRUST_CONCERNS = 'Trust Concerns',
    CONFLICT_RESOLUTION = 'Conflict Resolution',
    LONG_DISTANCE_RELATIONSHIP = 'Long Distance Relationship',
    BREAKUP_DIVORCE = 'Breakup or Divorce',

    // Career
    JOB_SATISFACTION = 'Job Satisfaction',
    WORK_LIFE_BALANCE = 'Work-Life Balance',
    PROFESSIONAL_GROWTH = 'Professional Growth',
    WORKPLACE_RELATIONSHIPS = 'Workplace Relationships',
    JOB_SEARCH_UNEMPLOYMENT = 'Job Search or Unemployment',
    PERFORMANCE_PRESSURE = 'Performance Pressure',
    CAREER_TRANSITION = 'Career Transition',

    // Health
    PHYSICAL_WELLBEING = 'Physical Wellbeing',
    MENTAL_HEALTH = 'Mental Health',
    SLEEP_ISSUES = 'Sleep Issues',
    FITNESS_GOALS = 'Fitness Goals',
    CHRONIC_ILLNESS = 'Chronic Illness',
    MEDICAL_CONCERNS = 'Medical Concerns',
    NUTRITION_DIET = 'Nutrition and Diet',

    // Finance
    BUDGETING_SPENDING = 'Budgeting and Spending',
    DEBT_MANAGEMENT = 'Debt Management',
    SAVING_CHALLENGES = 'Saving Challenges',
    INCOME_CONCERNS = 'Income Concerns',
    FINANCIAL_PLANNING = 'Financial Planning',
    INVESTMENT_DECISIONS = 'Investment Decisions',
    UNEXPECTED_EXPENSES = 'Unexpected Expenses',

    // Personal Growth
    SELF_ESTEEM_CONFIDENCE = 'Self-Esteem and Confidence',
    IDENTITY_QUESTIONS = 'Identity Questions',
    SETTING_BOUNDARIES = 'Setting Boundaries',
    HABIT_FORMATION = 'Habit Formation',
    GOAL_ACHIEVEMENT = 'Goal Achievement',
    FINDING_PURPOSE = 'Finding Purpose',
    PERSONAL_VALUES = 'Personal Values',

    // Family
    PARENTING_CHALLENGES = 'Parenting Challenges',
    EXTENDED_FAMILY_DYNAMICS = 'Extended Family Dynamics',
    SIBLING_RELATIONSHIPS = 'Sibling Relationships',
    CARETAKING_RESPONSIBILITIES = 'Caretaking Responsibilities',
    FAMILY_CONFLICT = 'Family Conflict',
    CULTURAL_EXPECTATIONS = 'Cultural Expectations',
    FAMILY_TRANSITIONS = 'Family Transitions',

    // Social Life
    FRIENDSHIP_MAINTENANCE = 'Friendship Maintenance',
    MAKING_NEW_CONNECTIONS = 'Making New Connections',
    SOCIAL_ANXIETY = 'Social Anxiety',
    GROUP_DYNAMICS = 'Group Dynamics',
    LONELINESS = 'Loneliness',
    SOCIAL_EVENTS_ACTIVITIES = 'Social Events and Activities',
    ONLINE_RELATIONSHIPS = 'Online Relationships',

    // Home Environment
    LIVING_SITUATION = 'Living Situation',
    ROOMMATE_ISSUES = 'Roommate Issues',
    HOME_ORGANIZATION = 'Home Organization',
    MOVING_RELOCATION = 'Moving or Relocation',
    NEIGHBORHOOD_CONCERNS = 'Neighborhood Concerns',
    HOUSEHOLD_RESPONSIBILITIES = 'Household Responsibilities',
    HOME_IMPROVEMENT = 'Home Improvement',

    // Education
    ACADEMIC_PERFORMANCE = 'Academic Performance',
    LEARNING_CHALLENGES = 'Learning Challenges',
    STUDY_HABITS = 'Study Habits',
    SCHOOL_LIFE_BALANCE = 'School-Life Balance',
    EDUCATIONAL_DECISIONS = 'Educational Decisions',
    STUDENT_RELATIONSHIPS = 'Student Relationships',
    LEARNING_NEW_SKILLS = 'Learning New Skills',

    // Spirituality
    BELIEF_QUESTIONS = 'Belief Questions',
    SPIRITUAL_PRACTICES = 'Spiritual Practices',
    COMMUNITY_CONNECTION = 'Community Connection',
    FINDING_MEANING = 'Finding Meaning',
    VALUES_ALIGNMENT = 'Values Alignment',
    SPIRITUAL_GROWTH = 'Spiritual Growth',
    RELIGIOUS_OBLIGATIONS = 'Religious Obligations',

    // Other
    TIME_MANAGEMENT = 'Time Management',
    CREATIVE_BLOCKS = 'Creative Blocks',
    CURRENT_EVENTS_NEWS = 'Current Events and News',
    WEATHER_SEASONAL_IMPACTS = 'Weather and Seasonal Impacts',
    TECHNOLOGY_CHALLENGES = 'Technology Challenges',
    UNEXPECTED_LIFE_CHANGES = 'Unexpected Life Changes',
    LEGAL_MATTERS = 'Legal Matters',
}

export enum JournalVisibility {
    PRIVATE = 'private',
    SHARED = 'shared',
    SHARED_AS_ECHO = 'shared-as-echo',
}

export enum JournalStatus {
    DRAFT = 'draft',
    PUBLISHED = 'published',
    READYFORINFERENCE = 'ready-for-inference',
    FAILED_INFERENCE = 'published-with-failed-inference',
}

export enum PersonalityType {
    ISTJ = 'ISTJ',
    ISFJ = 'ISFJ',
    INFJ = 'INFJ',
    INTJ = 'INTJ',
    ISTP = 'ISTP',
    ISFP = 'ISFP',
    INFP = 'INFP',
    INTP = 'INTP',
    ESTP = 'ESTP',
    ESFP = 'ESFP',
    ENFP = 'ENFP',
    ENTP = 'ENTP',
    ESTJ = 'ESTJ',
    ESFJ = 'ESFJ',
    ENFJ = 'ENFJ',
    ENTJ = 'ENTJ',
    Conscientiousness = 'Conscientiousness',
    Openness = 'Openness',
    Extraversion = 'Extraversion',
    Agreeableness = 'Agreeableness',
    Neuroticism = 'Neuroticism',
}

export enum GoalStatus {
    BACKLOG = 'backlog',
    COMPLETED = 'completed',
}

export enum GoalVisibility {
    PRIVATE = 'private',
    SHARED = 'shared',
}

export enum GoalType {
    TODAY = 'today',
    FUTURE = 'future',
}

export enum IdentityPanel {
    FivePanel = 'five-panel',
    SixteenPanel = 'sixteen-panel',
}

/*
 * Medical Intakes
 */
export enum ProblemSeverity {
    MINOR,
    MILD,
    MODERATE,
    SEVERE,
    VERY_SEVERE,
}

export enum DrinksPerDay {
    LT_ONE,
    ONE,
    TWO,
    THREE,
    GTE_FOUR,
}

export enum RecDrugFrequency {
    DAILY,
    WEEKLY,
    MONTHLY,
}

export enum ExerciseFrequency {
    NA,
    MONTHLY,
    WEEKLY,
    DAILY,
}

export enum SleepQuality {
    VERY_POOR,
    POOR,
    FAIR,
    GOOD,
    EXCELLENT,
}

/*
 * Drugs
 */
export enum DrugType {
    OPIOID = 'opioid',
    RELAXANT = 'relaxant',
    SUPPLEMENT = 'supplement',
    HALLUCINOGEN = 'hallucinogen',
    STIMULANT = 'stimulant',
    PRESCRIPTION = 'prescription',
}

/*
 * Client Plans
 */
export enum ClientPlanType {
    TREATMENT = 'treatment',
    SAFETY = 'safety',
}

/*
 * Videos
 */
export enum VideoCategory {
    TRAINING = 'training',
}

/*
 * Conversations
 */
export enum MessageDirection {
    INBOUND = 'inbound',
    OUTBOUND = 'outbound',
}

export enum MessageStatus {
    // Outbound
    ACCEPTED = 'accepted',
    QUEUED = 'queued',
    SCHEDULED = 'scheduled',
    SENDING = 'sending',
    SENT = 'sent',
    DELIVERY_UNKNOWN = 'delivery_unknown',
    DELIVERED = 'delivered',
    UNDELIVERED = 'undelivered',
    FAILED = 'failed',

    UNSENT = 'unsent',

    // Inbound
    RECEIVED = 'received',

    // Email Specific
    BOUNCED = 'bounced',
    DEFERRED = 'deferred',
    PROCESSED = 'processed',
    DROPPED = 'dropped',
    OPENED = 'opened',
    CLICKED = 'clicked',
}

/*
 * Analytics & Session Data
 */
export enum ClinicalTechnique {
    MOOD_CHECK = 'Mood Check',
    BRIDGE = 'Bridge',
    AGENDA_SETTING = 'Agenda Setting',
    CBT_SKILLS = 'CBT Skills',
    ACTION_PLAN = 'Action Plan',
    SESSION_SUMMARY = 'Session Summary',
    SOCRATIC_QUESTIONING = 'Socratic Questioning',
    REFLECTIVE_LISTENING = 'Reflective Listening',
    EMPATHIC_COMMUNICATION = 'Empathic Communication',
}

export enum QuestionnaireQuestionType {
    YES_NO = 'yes_no',
    MULTIPLE_CHOICE = 'multiple_choice',
    FREE_RESPONSE = 'free_response',
}

export enum QuestionnaireQuestionIntepretationType {
    CLASSIFICATION = 'classification',
    CONCLUSION = 'conclusion',
}

/*
 * Permissions
 */

export enum GeneralPermission {
    // Write
    WRITE_CARRIERS = 'write_carriers',
    WRITE_ADMINS = 'write_admins',

    // Delete
    DELETE_USERS = 'delete_users',
}

export enum AppointmentPermission {
    // Read
    READ_GENERAL = 'read_appointments_general',
    READ_ZOOM = 'read_appointments_zoom',
    READ_PAYMENTS = 'read_appointments_payments',
    READ_SESSION_DATA = 'read_appointments_session_data',
    READ_TRANSCRIPTIONS = 'read_appointments_transcriptions',
    READ_DIAGNOSES = 'read_appointments_diagnoses',
    READ_NOTES = 'read_appointments_notes',

    // Write
    WRITE_DIAGNOSES = 'write_appointments_diagnoses',
    WRITE_FIRST_TIME_DIAGNOSES = 'write_appointments_first_time_diagnoses',
    WRITE_NOTES = 'write_appointments_notes',

    // Special
    UNLOCK_DIAGNOSES = 'unlock_appointments_diagnoses',
}

export enum ClientPermission {
    // Read
    READ_GENERAL = 'read_client_general',
    READ_INTAKE = 'read_client_intake',
    READ_ASSESSMENTS = 'read_client_assessments',
    READ_JOURNALS = 'read_client_journals',
    READ_PLANS = 'read_client_plans',
    READ_PAYMENT_METHODS = 'read_client_payment_methods',
    READ_PAYMENTS = 'read_client_payments',
    READ_POLICIES = 'read_client_policies',
    READ_CLAIMS = 'read_client_claims',
    READ_UPLOADS = 'read_client_uploads',
    READ_CONVERSATIONS = 'read_client_conversations',
    READ_CAREBOT_CONVERSATIONS = 'read_client_carebot_conversations',
    READ_ASSIGNMENT_SUBMISSIONS = 'read_assignment_submissions',
    READ_TRUSTED_CONTACTS = 'read_trusted_contacts',

    // Write
    WRITE_GENERAL = 'write_client_general',
    /** Fill in and submit a client's medical intake on their behalf, while it's incomplete */
    WRITE_INTAKE = 'write_client_intake',
    WRITE_CREATION = 'write_client_creation',
    WRITE_REFUNDS = 'write_client_refunds',
    WRITE_CLAIMS = 'write_client_claims',
    WRITE_UPLOADS = 'write_client_uploads',
    WRITE_PAYMENT_METHODS = 'write_client_payment_methods',
    WRITE_POLICIES = 'write_client_policies',
    WRITE_CONVERSATIONS = 'write_client_conversations',
    WRITE_BETA_ACCESS = 'write_client_beta_access',
    WRITE_ASSIGNMENT_SUBMISSIONS = 'write_assignment_submissions',
    WRITE_TRUSTED_CONTACTS = 'write_trusted_contacts',

    // Delete
    DELETE_POLICIES = 'delete_client_policies',
}

export enum ProviderPermission {
    // Read
    READ_GENERAL = 'read_provider_general',
    READ_AVAILABILITY = 'read_provider_availability',
    READ_CONVERSATIONS = 'read_provider_conversations',
    READ_PAYOUTS = 'read_provider_payouts',
    READ_ANALYTICS = 'read_provider_analytics',
    READ_CREDENTIALING_DATA = 'read_provider_credentialing_data',
    READ_UPLOADS = 'read_provider_uploads',

    // Write
    WRITE_GENERAL = 'write_provider_general',
    WRITE_CREATION = 'write_provider_creation',
    WRITE_CREDENTIALING_DATA = 'write_provider_credentialing_data',
    WRITE_AVAILABILITY = 'write_provider_availability',
    WRITE_UPLOADS = 'write_provider_uploads',
    WRITE_RECRUITING = 'write_provider_recruiting',

    // Special
    FORCE_AVAILABILITY = 'force_provider_availability',
}

/*
 * Groups
 */
export enum GroupEventStatus {
    DRAFT = 'draft',
    REJECTED = 'rejected',
    APPROVED = 'approved',
    ACTIVE = 'active',
    SUSPENDED = 'suspended',
    APPROVAL_REQUESTED = 'approval_requested',
    REACTIVATED = 'reactivated',
}
export enum GroupMemberStatus {
    BLOCKED = 'blocked',
    SUSPENDED = 'suspended',
    REPORT = 'report',
    UNBLOCKED = 'unblocked',
    REACTIVATED = 'reactivated',
    REMOVED = 'removed',
    JOINED = 'joined',
    WAITLISTED = 'waitlisted',
    LEFT = 'left',
}

export enum DayOfWeek {
    MON = 1,
    TUES,
    WED,
    THURS,
    FRI,
    SAT,
    SUN,
}

export enum GroupAvailability {
    MORNING = 'morning',
    AFTERNOON = 'afternoon',
    EVENING = 'evening',
    MON = 'mon',
    TUES = 'tues',
    WED = 'wed',
    THURS = 'thurs',
    FRI = 'fri',
    SAT = 'sat',
    SUN = 'sun',
}

export enum GroupSortBy {
    NAME = 'name',
    CREATEDAT = 'createdAt',
    POPULARITY = 'popularity',
}

export enum GroupProfitStatus {
    PROFITABLE = 'Profitable',
    LOSS = 'Loss',
}

export enum SortOrder {
    ASC = 'asc',
    DESC = 'desc',
}

export enum GroupSessionStatus {
    CONFIRMED = 'confirmed',
    RESCHEDULED = 'rescheduled',
    COMPLETED = 'completed',
    CANCELED = 'canceled',
    MISSED_BY_PROVIDER = 'missed_by_provider',
    DELETED = 'deleted',
}

export enum GroupMembershipModerationAction {
    BLOCK = 'block',
    REMOVE = 'remove',
}

export enum RecurrenceFrequency {
    WEEKLY = 'weekly',
}

export enum SubscriptionStatus {
    INACTIVE = 'inactive',
    UNVERIFIED = 'unverified',
    ACTIVE = 'active',
    CANCELLED = 'cancelled',
    EXPIRED = 'expired',
}

export enum PricingStatus {
    AVAILABLE = 'available',
    AVAILABLE_GRANDFATHERED = 'available_grandfathered',
    NOT_AVAILABLE = 'not_available',
}

export enum RsvpStatus {
    YES = 'yes',
    NO = 'no',
    MAYBE = 'maybe',
    NO_RESPONSE = 'no_response',
}

export enum GroupPromoIneligibilityReason {
    ALREADY_SUBSCRIBED = 'ALREADY_SUBSCRIBED',
    ALREADY_REDEEMED = 'ALREADY_REDEEMED',
}

export interface GroupPromoCodeEligibility {
    canRedeem: boolean;
    /** Null when canRedeem is true. */
    reason: GroupPromoIneligibilityReason | null;
}

export enum GroupPromoCodeRejectionReason {
    NOT_FOUND = 'NOT_FOUND',
    INACTIVE = 'INACTIVE',
    EXPIRED = 'EXPIRED',
    EXHAUSTED = 'EXHAUSTED',
    ALREADY_SUBSCRIBED = 'ALREADY_SUBSCRIBED',
    ALREADY_REDEEMED = 'ALREADY_REDEEMED',
    NOT_ELIGIBLE = 'NOT_ELIGIBLE',
    MINIMUM_NOT_MET = 'MINIMUM_NOT_MET',
    UNAVAILABLE = 'UNAVAILABLE',
}

export enum GroupPromoDiscountDuration {
    ONCE = 'ONCE',
    REPEATING = 'REPEATING',
    FOREVER = 'FOREVER',
}

/*
 * Messaging
 */
export enum MessagingParticipantRole {
    CLIENT = 'client',
    PROVIDER = 'provider',
}

export enum ConversationKind {
    MESSAGING = 'messaging',
    SMS = 'sms',
    EMAIL = 'email',
    CAREBOT = 'carebot',
}

export enum MessageKind {
    USER_TEXT = 'user_text',
    SYS_ESCALATION_CREATED = 'sys_escalation_created',
    SYS_ESCALATION_RESOLVED = 'sys_escalation_resolved',
    SYS_ESCALATION_REOPENED = 'sys_escalation_reopened',
    SYS_CRISIS_FLAG = 'sys_crisis_flag',
}

export enum MessageContentFormat {
    PLAIN = 'plain',
    MARKDOWN = 'markdown',
    HTML = 'html',
}

export enum EscalationStatus {
    PENDING = 'pending',
    RESOLVED = 'resolved',
}

export enum ReportedMessageKind {
    CRISIS = 'crisis',
}

export enum ReportedMessageSeverity {
    LOW = 'low',
    MEDIUM = 'medium',
    HIGH = 'high',
    CRITICAL = 'critical',
}

export enum MessageReportTheme {
    GENERAL = 'General Mental Health Crisis',
    SUICIDE_THOUGHTS = 'Suicidal Thoughts',
    URGENT_SELF_HARM = 'Self-Harm',
    URGENT_EMOTIONAL_DISTRESS = 'Urgent Emotional Distress',
    PLAN_TO_SELF_HARM = 'Plan to Self-Harm',
    SEVERE_DEPRESSION = 'Severe Depression',
    HOPELESSNESS = 'Hopelessness',
    PSYCHOSIS = 'Psychosis',
    CONFUSION = 'Confusion',
    DISSOCIATION = 'Losing Touch with Reality',
    TRAUMA = 'Trauma',
    ASSAULT = 'Assault',
    ABUSE = 'Abuse',
    OVERDOSE = 'Overdose',
    SUBSTANCE_ABUSE = 'Substance Crisis',
    PANIC = 'Panic / Extreme Anxiety',
}

export enum EscalationSortBy {
    CREATED_AT = 'createdAt',
    UPDATED_AT = 'updatedAt',
    STATUS = 'status',
}

export enum CCRewardStatus {
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    REDEEMED = 'REDEEMED',
}

export enum ONBOARDING_STEPS {
    GENERAL_INFORMATION = 'generalInformation',
    INSURANCE_INFORMATION = 'insuranceInformation',
    EMERGENCY_CONTACT = 'emergencyContact',
    PROVIDER_INFORMATION = 'providerInformation',
}

export enum SubscriptionType {
    REACHLINK_PLUS = 'REACHLINK_PLUS',
}

export enum SubscriptionPlatform {
    APPLE = 'APPLE',
    GOOGLE = 'GOOGLE',
    WEB = 'WEB',
}

export enum SubscriptionInterval {
    MONTHLY = 'MONTHLY',
    YEARLY = 'YEARLY',
}

export enum SubscriptionFeature {
    JOURNALS = 'JOURNALS',
    CAREBOT = 'CAREBOT',
}

export enum ClinicalAiRestrictionReason {
    AGE = 'age',
    STATE = 'state',
    COUNTRY = 'country',
}

/**
 * Cronofy's `provider_name` values for a connected calendar profile. Mirrors the backend's
 * enums/shared/shared.enums.ts — only add a member once the exact string Cronofy returns
 * for that provider has been confirmed.
 */
export enum CronofyProviderName {
    GOOGLE = 'google',
    APPLE = 'apple',
    LIVE_CONNECT = 'live_connect',
}
